// src/pages/Services.jsx - Updated with Social Sharing
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiCode,
  FiTrendingUp,
  FiZap,
  FiArrowRight,
  FiCheck,
  FiStar,
  FiUsers,
  FiClock,
  FiAward,
  FiHeart,
  FiLayers,
  FiPenTool,
  FiMonitor,
  FiSmartphone,
  FiGlobe,
  FiPackage,
  FiImage,
  FiType,
  FiVideo,
  FiBarChart2,
  FiShield,
  FiMessageCircle,
  FiDollarSign,
  FiCalendar,
  FiBriefcase,
  FiSmile,
  FiGrid,
  FiCheckCircle,
  FiBookOpen,
  FiExternalLink,
} from 'react-icons/fi';
import { TbBrandFiverr } from 'react-icons/tb';
import {
  db,
  collection,
  getDocs,
  query,
  orderBy,
  where,
} from '../services/firebase';

import { TfiBrush } from 'react-icons/tfi';
import InquiryPopup from '../components/InquiryPopup';
import { useTheme } from '../contexts/ThemeContext';
import ReviewsSection from '../components/ReviewsSection';
import StatsSection from '../components/StatsSection';
import { FiverrCard } from '../components/FiverrCard';
import { motion, AnimatePresence } from 'framer-motion';
import { ogImageService } from '../services/ogImageService';

// =============================================
// GLASSY DIVIDER
// =============================================
const GlassyDivider = () => {
  return (
    <div className="relative py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="relative h-px w-full overflow-hidden">
          <motion.div
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute inset-0 w-1/3"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--text), transparent)',
              opacity: 0.1,
            }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================
// GLASSY CARD
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.4)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        boxShadow:
          '0 8px 32px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// LIQUID GRADIENT HERO
// =============================================
const ServicesHero = () => {
  return (
    <div className="relative mb-8 overflow-hidden rounded-3xl">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:from-blue-500/10 dark:via-transparent dark:to-purple-500/10" />

      <motion.div
        animate={{
          x: ['-20%', '15%', '-5%', '20%', '-20%'],
          y: ['-15%', '10%', '-10%', '15%', '-15%'],
          scale: [1, 1.3, 0.9, 1.2, 1],
          borderRadius: [
            '60% 40% 30% 70%',
            '40% 60% 70% 30%',
            '30% 70% 60% 40%',
            '70% 30% 40% 60%',
            '60% 40% 30% 70%',
          ],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-5 left-5 w-72 h-72 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #3b82f6 0%, #60a5fa 40%, transparent 70%)',
          opacity: 0.12,
        }}
      />

      <motion.div
        animate={{
          x: ['15%', '-15%', '10%', '-10%', '15%'],
          y: ['15%', '-10%', '20%', '-15%', '15%'],
          scale: [1.2, 0.9, 1.4, 0.8, 1.2],
          borderRadius: [
            '40% 60% 70% 30%',
            '60% 40% 30% 70%',
            '70% 30% 40% 60%',
            '30% 70% 60% 40%',
            '40% 60% 70% 30%',
          ],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-5 right-5 w-80 h-80 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #8b5cf6 0%, #a78bfa 40%, transparent 70%)',
          opacity: 0.1,
        }}
      />

      <motion.div
        animate={{
          x: ['-5%', '10%', '-10%', '5%', '-5%'],
          y: ['10%', '-15%', '5%', '-10%', '10%'],
          scale: [1, 1.2, 0.8, 1.3, 1],
          borderRadius: [
            '50% 30% 60% 40%',
            '30% 60% 40% 70%',
            '60% 40% 70% 30%',
            '40% 70% 30% 60%',
            '50% 30% 60% 40%',
          ],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #06b6d4 0%, #22d3ee 40%, transparent 70%)',
          opacity: 0.08,
        }}
      />

      <motion.div
        animate={{
          x: ['10%', '-20%', '15%', '-5%', '10%'],
          y: ['-5%', '15%', '-15%', '10%', '-5%'],
          scale: [0.8, 1.4, 0.9, 1.1, 0.8],
          borderRadius: [
            '70% 30% 50% 50%',
            '30% 70% 50% 50%',
            '50% 50% 70% 30%',
            '50% 50% 30% 70%',
            '70% 30% 50% 50%',
          ],
        }}
        transition={{
          duration: 22,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-20 left-20 w-60 h-60 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #ec4899 0%, #f472b6 40%, transparent 70%)',
          opacity: 0.06,
        }}
      />

      <div className="relative px-6 py-10 md:py-14 lg:py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-sm backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.6)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiBookOpen className="w-4 h-4" style={{ color: '#3b82f6' }} />
          <span style={{ color: 'var(--muted)' }}>What I Offer</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3 tracking-tight leading-[1.1]"
          style={{ color: 'var(--text)' }}
        >
          Transform your
          <span className="block" style={{ color: 'var(--muted)' }}>
            vision into reality
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-sm md:text-base max-w-2xl mx-auto leading-relaxed"
          style={{ color: 'var(--muted)', opacity: 0.8 }}
        >
          Comprehensive design solutions tailored to your brand's unique needs.
          From concept to completion, I've got you covered.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-6 flex items-center justify-center gap-3"
        >
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <span
            className="text-xs tracking-widest uppercase"
            style={{ color: 'var(--muted)', opacity: 0.3 }}
          >
            Explore
          </span>
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
        </motion.div>
      </div>
    </div>
  );
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

// =============================================
// SERVICE CARD WITH SLIDE OVERLAY
// =============================================
const ServiceCard = ({ service, onInquire, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      variants={itemVariants}
      className="group relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/service/${service.id}`} className="block h-full">
        <div
          className="absolute inset-0 rounded-2xl transition-all duration-500 group-hover:scale-105"
          style={{ backgroundColor: 'var(--border)', opacity: 0.5 }}
        />
        <div
          className="relative rounded-2xl transition-all duration-300 hover:translate-y-[-4px] overflow-hidden h-full"
          style={{
            border: `1px solid ${isHovered ? 'var(--text)' : 'var(--border)'}`,
            backgroundColor: 'var(--bg)',
          }}
        >
          {/* Popular Badge */}
          {service.popular && (
            <div
              className="absolute top-0 left-0 z-20 px-3 py-1 rounded-br-lg text-xs font-medium flex items-center gap-1"
              style={{
                backgroundColor: 'var(--text)',
                color: 'var(--bg)',
              }}
            >
              <FiStar className="w-3 h-3" />
              Most Popular
            </div>
          )}

          {/* Service Image - 16:9 */}
          <div className="relative w-full bg-black/5 dark:bg-white/5">
            <div className="relative w-full pt-[56.25%]">
              {service.imageUrl ? (
                <>
                  <img
                    src={service.imageUrl}
                    alt={service.title}
                    className="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </>
              ) : (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                  <service.icon
                    className="w-12 h-12"
                    style={{ color: 'var(--muted)' }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            <h3
              className="text-xl font-bold mb-2 line-clamp-1"
              style={{ color: 'var(--text)' }}
            >
              {service.title}
            </h3>
            <p
              className="mb-3 text-sm line-clamp-2"
              style={{ color: 'var(--muted)' }}
            >
              {service.description}
            </p>

            {/* Features */}
            <div className="mb-4 space-y-1.5">
              {service.features.slice(0, 2).map((feature, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 text-xs"
                  style={{ color: 'var(--muted)' }}
                >
                  <FiCheck
                    className="w-3 h-3"
                    style={{ color: 'var(--text)' }}
                  />
                  <span className="truncate">{feature}</span>
                </div>
              ))}
              {service.features.length > 2 && (
                <div className="text-xs ml-5" style={{ color: 'var(--muted)' }}>
                  +{service.features.length - 2} more features
                </div>
              )}
            </div>

            {/* Price & Duration */}
            <div
              className="pt-3 mt-2 border-t flex items-center justify-between"
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <div>
                <div className="text-2xl font-bold">${service.price}</div>
                <div className="text-xs" style={{ color: 'var(--muted)' }}>
                  Starting from
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 text-sm">
                  <FiClock className="w-3 h-3" />
                  <span>{service.duration}</span>
                </div>
              </div>
            </div>

            {/* Inquire Button with Slide Overlay Effect */}
            <div className="mt-4 relative overflow-hidden rounded-lg">
              <motion.div
                className="absolute inset-0 rounded-lg"
                style={{
                  background:
                    'linear-gradient(135deg, var(--text), var(--text))',
                  opacity: 0.08,
                }}
                initial={{ x: '-100%' }}
                animate={{ x: isHovered ? '0%' : '-100%' }}
                transition={{ duration: 0.4, ease: 'easeInOut' }}
              />

              <button
                onClick={e => onInquire(e, service)}
                className="relative w-full flex items-center justify-center gap-2 py-2.5 rounded-lg transition-all duration-300 hover:gap-3 cursor-pointer z-10"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <span className="text-sm">Inquire Now</span>
                <FiArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

function Services() {
  // ==================== ANIMATION ON SCROLL ====================
  const [animatedSections, setAnimatedSections] = useState({});
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // ==================== SERVICES DATA FROM FIREBASE ====================
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);

  // ==================== INTERACTIVE 3D CARD STATE ====================
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [pricingPlan, setPricingPlan] = useState('monthly');

  // ==================== SUBSCRIPTION PLANS STATE ====================
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');

  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  // ===== Generate OG Meta Data for Services Homepage =====
  const metaData = useMemo(() => {
    return ogImageService.servicesHome();
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      setLoadingSubscriptions(true);
      const q = query(
        collection(db, 'subscriptionPlans'),
        where('status', '==', 'active'),
        orderBy('order', 'asc'),
      );
      const snapshot = await getDocs(q);
      const plansList = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        plansList.push({
          id: doc.id,
          name: data.name,
          monthlyPrice: data.monthlyPrice,
          yearlyPrice: data.yearlyPrice,
          monthlyDuration: data.monthlyDuration || 'Month to month',
          yearlyDuration: data.yearlyDuration || 'Annual commitment',
          description: data.description,
          features: data.features || [],
          icon: getIconComponent(data.icon),
          popular: data.popular || false,
          ctaText: data.ctaText || 'Get Started',
          savingsBadge: data.savingsBadge,
        });
      });
      setSubscriptionPlans(plansList);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  // Map icon names from Firebase to actual components
  const getIconComponent = iconName => {
    const icons = {
      FiPenTool,
      FiMonitor,
      FiVideo,
      FiPackage,
      FiUsers,
      FiHeart,
      FiLayers,
      FiCode,
      FiTrendingUp,
      FiZap,
      FiStar,
      FiClock,
      FiAward,
      FiImage,
      FiType,
      FiBarChart2,
      FiShield,
      FiMessageCircle,
      FiDollarSign,
      FiCalendar,
      FiBriefcase,
      FiSmile,
      FiGlobe,
      FiSmartphone,
    };
    return icons[iconName] || FiPackage;
  };

  const serviceCategories = [
    { id: 'all', name: 'All Services', icon: FiLayers },
    { id: 'branding', name: 'Branding', icon: FiPenTool },
    { id: 'product', name: 'Product Design', icon: FiPackage },
    { id: 'others', name: 'Others', icon: FiGrid },
  ];

  const categoryIconMap = {
    branding: FiPenTool,
    product: FiPackage,
    others: FiGrid,
  };

  // ==================== FETCH SERVICES FROM FIREBASE ====================
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoadingServices(true);
        const q = query(
          collection(db, 'readyMadeServices'),
          where('status', '==', 'active'),
          orderBy('popular', 'desc'),
          orderBy('order', 'asc'),
        );
        const snapshot = await getDocs(q);
        const servicesList = [];

        snapshot.forEach(doc => {
          const data = doc.data();

          let category = data.category;
          if (category !== 'branding' && category !== 'product') {
            category = 'others';
          }

          const service = {
            id: doc.id,
            title: data.name,
            description: data.description,
            category: category,
            icon:
              getIconComponent(data.icon) ||
              categoryIconMap[category] ||
              FiPackage,
            features: data.features || [],
            price: data.price,
            duration: data.duration,
            popular: data.popular || false,
            imageUrl: data.imageUrl || null,
          };
          servicesList.push(service);
        });

        setServices(servicesList);
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices([]);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  // ==================== ANIMATION ON SCROLL ====================
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setAnimatedSections(prev => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -100px 0px' },
    );

    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // ==================== HANDLE INQUIRY CLICK ====================
  const handleInquireClick = (e, service) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedPackage({
      id: service.id,
      name: service.title,
      price: service.price,
      duration: service.duration,
      description: service.description,
      features: service.features,
      popular: service.popular || false,
      imageUrl: service.imageUrl,
      category: service.category,
      gradient: service.gradient || 'from-purple-500 to-pink-500',
    });
    setIsPopupOpen(true);
  };

  // ==================== FILTERED SERVICES ====================
  const filteredServices =
    selectedCategory === 'all'
      ? services
      : services.filter(service => service.category === selectedCategory);

  // ==================== IMPROVED SKELETON LOADING CARDS ====================
  const SkeletonCard = () => (
    <div className="group relative cursor-pointer">
      <div
        className="absolute inset-0 rounded-2xl transition-all duration-500"
        style={{ backgroundColor: 'var(--border)', opacity: 0.5 }}
      />
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          border: `1px solid var(--border)`,
          backgroundColor: 'var(--bg)',
        }}
      >
        <div className="relative w-full pt-[56.25%] bg-black/5 dark:bg-white/5 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" />
          </div>
        </div>
        <div className="p-5">
          <div
            className="h-6 w-3/4 rounded mb-3 animate-pulse"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <div
            className="h-4 w-full rounded mb-2 animate-pulse"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <div
            className="h-4 w-2/3 rounded mb-4 animate-pulse"
            style={{ backgroundColor: 'var(--border)' }}
          />
          {[1, 2].map(i => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <div
                className="w-3 h-3 rounded animate-pulse"
                style={{ backgroundColor: 'var(--border)' }}
              />
              <div
                className="h-3 w-24 rounded animate-pulse"
                style={{ backgroundColor: 'var(--border)' }}
              />
            </div>
          ))}
          <div
            className="pt-3 mt-2 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex justify-between items-center mb-3">
              <div
                className="h-7 w-20 rounded animate-pulse"
                style={{ backgroundColor: 'var(--border)' }}
              />
              <div
                className="h-4 w-16 rounded animate-pulse"
                style={{ backgroundColor: 'var(--border)' }}
              />
            </div>
            <div
              className="h-10 w-full rounded-lg animate-pulse"
              style={{ backgroundColor: 'var(--border)' }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // ==================== PROCESS STEPS ====================
  const processSteps = [
    {
      number: '01',
      title: 'Discovery',
      description: 'We discuss your goals, vision, and requirements',
      icon: FiMessageCircle,
      duration: '1-2 days',
      gradient: 'from-blue-500/20 to-blue-600/10',
    },
    {
      number: '02',
      title: 'Research',
      description: 'Deep dive into market analysis and competitor research',
      icon: FiBarChart2,
      duration: '2-3 days',
      gradient: 'from-purple-500/20 to-purple-600/10',
    },
    {
      number: '03',
      title: 'Design',
      description: 'Creating concepts and refining based on feedback',
      icon: TfiBrush,
      duration: '1-2 weeks',
      gradient: 'from-pink-500/20 to-pink-600/10',
    },
    {
      number: '04',
      title: 'Delivery',
      description: 'Final polish and handoff of all assets',
      icon: FiPackage,
      duration: '1-2 days',
      gradient: 'from-green-500/20 to-green-600/10',
    },
  ];

  // Inject shimmer animation
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }
      .animate-shimmer {
        animation: shimmer 1.8s infinite;
      }
      @keyframes pulse-slow {
        0%, 100% {
          opacity: 0.4;
        }
        50% {
          opacity: 0.8;
        }
      }
      .animate-pulse-slow {
        animation: pulse-slow 2s ease-in-out infinite;
      }
      .animation-delay-400 {
        animation-delay: 400ms;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {metaData?.metaTitle || 'Services | WD Hajbul - Design & Branding'}
        </title>
        <meta
          name="description"
          content={
            metaData?.metaDescription ||
            'Professional logo design, vector tracing, and product visuals.'
          }
        />
        <meta
          name="keywords"
          content="design services, branding, logo design, product design, vector tracing, graphic design"
        />
        <link
          rel="canonical"
          href={metaData?.canonical || window.location.href}
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={metaData?.og?.url || window.location.href}
        />
        <meta
          property="og:title"
          content={
            metaData?.og?.title || 'Services | WD Hajbul - Design & Branding'
          }
        />
        <meta
          property="og:description"
          content={
            metaData?.og?.description ||
            'Professional logo design, vector tracing, and product visuals.'
          }
        />
        <meta property="og:image" content={metaData?.og?.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta
          property="og:site_name"
          content={metaData?.og?.siteName || 'WD Hajbul Creative Studio'}
        />
        <meta property="og:locale" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:url"
          content={metaData?.og?.url || window.location.href}
        />
        <meta
          name="twitter:title"
          content={
            metaData?.twitter?.title ||
            'Services | WD Hajbul - Design & Branding'
          }
        />
        <meta
          name="twitter:description"
          content={
            metaData?.twitter?.description ||
            'Professional logo design, vector tracing, and product visuals.'
          }
        />
        <meta name="twitter:image" content={metaData?.twitter?.image} />
        <meta name="twitter:site" content="@wdhejbul" />
        <meta name="twitter:creator" content="@wdhejbul" />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* ==================== HERO SECTION - Liquid Gradient ==================== */}
        <section className="pt-32 pb-20 max-w-7xl mx-auto max-sm:p-5 max-sm:pt-24">
          <ServicesHero />
        </section>

        <StatsSection
          fetchProjectCount={true}
          columns={4}
          variant="default"
          showBorder={true}
        />

        <GlassyDivider />

        {/* ==================== SERVICES GRID WITH CATEGORY FILTER ==================== */}
        <section className="animate-on-scroll py-20 px-4 max-w-6xl mx-auto transition-all duration-700 delay-100">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {serviceCategories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className="flex items-center gap-2 px-5 py-2 rounded-full transition-all duration-300 backdrop-blur-sm"
                style={{
                  backgroundColor:
                    selectedCategory === category.id
                      ? 'var(--text)'
                      : 'rgba(var(--bg-rgb), 0.3)',
                  color:
                    selectedCategory === category.id
                      ? 'var(--bg)'
                      : 'var(--text)',
                  border:
                    selectedCategory === category.id
                      ? 'none'
                      : '1px solid rgba(var(--text-rgb), 0.06)',
                }}
              >
                <category.icon className="w-4 h-4" />
                <span className="text-sm">{category.name}</span>
              </button>
            ))}
          </div>

          {/* Services Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {loadingServices ? (
              Array(6)
                .fill(0)
                .map((_, idx) => <SkeletonCard key={`skeleton-${idx}`} />)
            ) : filteredServices.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <FiPackage
                    className="w-10 h-10"
                    style={{ color: 'var(--muted)' }}
                  />
                </div>
                <h3
                  className="text-xl font-semibold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  No services found
                </h3>
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  Check back soon for new services in this category!
                </p>
              </div>
            ) : (
              filteredServices.map((service, index) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  onInquire={handleInquireClick}
                  index={index}
                />
              ))
            )}
          </motion.div>
        </section>

        <GlassyDivider />

        {/* ==================== PROCESS SECTION - With Hover Gradient Overlay ==================== */}
        <section
          className="animate-on-scroll py-20 px-4 transition-all duration-700 delay-200"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-sm uppercase tracking-wider opacity-60 mb-4 block">
                How It Works
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Simple, transparent
                <br />
                <span className="italic">design process</span>
              </h2>
              <p
                className="max-w-2xl mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                A streamlined approach that ensures quality and efficiency
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                  className="relative group"
                >
                  {idx < processSteps.length - 1 && (
                    <div
                      className="hidden lg:block absolute top-20 left-[60%] w-[30%] h-px"
                      style={{ backgroundColor: 'var(--text)', opacity: 0.2 }}
                    />
                  )}

                  <div
                    className="relative rounded-2xl p-6 text-center transition-all duration-500 overflow-hidden"
                    style={{
                      backgroundColor: 'var(--bg)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <motion.div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background: `linear-gradient(135deg, ${step.gradient}, transparent 70%)`,
                      }}
                    />

                    <motion.div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                      style={{
                        background: `radial-gradient(circle at center, ${step.gradient.split(' ')[0].replace('from-', '').replace('/20', '/10')}, transparent 70%)`,
                      }}
                    />

                    <div className="relative z-10">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 relative">
                        <div
                          className="absolute inset-0 rounded-full animate-ping opacity-20 group-hover:opacity-40 transition-opacity duration-300"
                          style={{ backgroundColor: 'var(--text)' }}
                        />
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center relative z-10 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg"
                          style={{
                            backgroundColor: 'var(--text)',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                          }}
                        >
                          <step.icon
                            className="w-6 h-6 transition-transform duration-300 group-hover:rotate-6"
                            style={{ color: 'var(--bg)' }}
                          />
                        </div>
                      </div>
                      <div
                        className="text-3xl font-bold mb-2 transition-all duration-300 group-hover:scale-110"
                        style={{
                          color: 'var(--text)',
                          opacity: 0.15,
                        }}
                      >
                        {step.number}
                      </div>
                      <h3
                        className="font-semibold mb-2 transition-colors duration-300 group-hover:text-blue-600 dark:group-hover:text-blue-400"
                        style={{ color: 'var(--text)' }}
                      >
                        {step.title}
                      </h3>
                      <p
                        className="text-sm mb-3 transition-colors duration-300"
                        style={{ color: 'var(--muted)' }}
                      >
                        {step.description}
                      </p>
                      <div
                        className="text-xs transition-colors duration-300 group-hover:text-blue-600 dark:group-hover:text-blue-400"
                        style={{ color: 'var(--muted)', opacity: 0.6 }}
                      >
                        {step.duration}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== SUBSCRIPTION PLANS SECTION ==================== */}
        <section className="animate-on-scroll py-20 px-4 max-w-6xl mx-auto transition-all duration-700 delay-300">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-wider opacity-60 mb-4 block">
              Subscription Plans
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Monthly & Yearly <span className="italic">subscriptions</span>
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'var(--muted)' }}
            >
              Flexible subscription options for ongoing design needs
            </p>

            <div
              className="inline-flex p-1 rounded-full mt-6"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <button
                onClick={() => setPricingPlan('monthly')}
                className={`px-6 py-2 rounded-full text-sm transition-all duration-300 ${
                  pricingPlan === 'monthly'
                    ? 'bg-black dark:bg-white text-white dark:text-black'
                    : 'text-black dark:text-white'
                }`}
                style={{
                  backgroundColor:
                    pricingPlan === 'monthly'
                      ? isDark
                        ? '#ffffff'
                        : '#000000'
                      : 'transparent',
                  color:
                    pricingPlan === 'monthly'
                      ? isDark
                        ? '#000000'
                        : '#ffffff'
                      : 'var(--text)',
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => setPricingPlan('yearly')}
                className={`px-6 py-2 rounded-full text-sm transition-all duration-300 ${
                  pricingPlan === 'yearly'
                    ? 'bg-black dark:bg-white text-white dark:text-black'
                    : 'text-black dark:text-white'
                }`}
                style={{
                  backgroundColor:
                    pricingPlan === 'yearly'
                      ? isDark
                        ? '#ffffff'
                        : '#000000'
                      : 'transparent',
                  color:
                    pricingPlan === 'yearly'
                      ? isDark
                        ? '#000000'
                        : '#ffffff'
                      : 'var(--text)',
                }}
              >
                Yearly <span className="text-xs opacity-70">Save 15%</span>
              </button>
            </div>
          </div>

          {loadingSubscriptions ? (
            <div className="flex justify-center py-20">
              <div
                className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin"
                style={{ borderColor: 'var(--text)' }}
              />
            </div>
          ) : subscriptionPlans.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg" style={{ color: 'var(--muted)' }}>
                Subscription plans coming soon!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subscriptionPlans.map((plan, index) => {
                const currentPrice =
                  selectedBillingCycle === 'monthly'
                    ? plan.monthlyPrice
                    : plan.yearlyPrice;
                const currentDuration =
                  selectedBillingCycle === 'monthly'
                    ? plan.monthlyDuration
                    : plan.yearlyDuration;

                return (
                  <div
                    key={plan.id}
                    className={`group relative rounded-2xl transition-all duration-300 hover:translate-y-[-4px] cursor-pointer ${
                      plan.popular ? 'ring-2' : ''
                    }`}
                    style={{
                      border: `1px solid var(--border)`,
                      backgroundColor: 'var(--bg)',
                      ringColor: 'var(--text)',
                    }}
                  >
                    {plan.popular && (
                      <div
                        className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap z-10"
                        style={{
                          backgroundColor: 'var(--text)',
                          color: 'var(--bg)',
                        }}
                      >
                        Most Popular
                      </div>
                    )}

                    <div className="p-6">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <plan.icon
                          className="w-6 h-6"
                          style={{ color: 'var(--text)' }}
                        />
                      </div>

                      <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                      <p
                        className="text-sm mb-4 line-clamp-2"
                        style={{ color: 'var(--muted)' }}
                      >
                        {plan.description}
                      </p>

                      <div className="mb-3">
                        <span className="text-3xl font-bold">
                          ${currentPrice}
                        </span>
                        <span
                          className="text-sm"
                          style={{ color: 'var(--muted)' }}
                        >
                          /{selectedBillingCycle === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      </div>

                      <div
                        className="flex items-center gap-1 text-xs mb-4"
                        style={{ color: 'var(--muted)' }}
                      >
                        <FiClock className="w-3 h-3" />
                        <span>{currentDuration}</span>
                      </div>

                      <ul className="space-y-1.5 mb-4">
                        {plan.features.slice(0, 3).map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            <FiCheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0 text-green-500" />
                            <span className="line-clamp-1">{feature}</span>
                          </li>
                        ))}
                        {plan.features.length > 3 && (
                          <li
                            className="text-xs ml-5"
                            style={{ color: 'var(--muted)' }}
                          >
                            +{plan.features.length - 3} more features
                          </li>
                        )}
                      </ul>

                      <NavLink
                        to="/subscription"
                        className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg transition-all duration-300 group-hover:gap-3"
                        style={{
                          backgroundColor: plan.popular
                            ? 'var(--text)'
                            : 'transparent',
                          color: plan.popular ? 'var(--bg)' : 'var(--text)',
                          border: !plan.popular
                            ? '1px solid var(--border)'
                            : 'none',
                        }}
                      >
                        <span className="text-sm">View Details</span>
                        <FiArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </NavLink>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <GlassyDivider />

        {/* ==================== CUSTOM WORK SECTION ==================== */}
        <section
          className="animate-on-scroll py-20 px-4 transition-all duration-700 delay-400"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 mb-6">
              <FiHeart className="w-5 h-5" />
              <h2 className="text-2xl md:text-3xl font-bold">
                Need something custom?
              </h2>
            </div>
            <p className="mb-8" style={{ color: 'var(--muted)' }}>
              Every project is unique. Let's discuss your specific requirements
              and create a tailored solution just for you.
            </p>
            <NavLink
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full transition-all duration-300 hover:gap-3"
              style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
            >
              Let's Talk
              <FiArrowRight className="w-4 h-4" />
            </NavLink>
          </div>
        </section>

        <GlassyDivider />

        {/* ==================== FIVERR CARD - Reusable ==================== */}
        <section className="py-12 px-4 max-w-7xl mx-auto">
          <FiverrCard variant="standard" />
        </section>

        {/* ==================== FAQ SECTION ==================== */}
        <section className="animate-on-scroll py-20 px-4 max-w-4xl mx-auto transition-all duration-700 delay-500">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-wider opacity-60 mb-4 block">
              Questions
            </span>
            <h2 className="text-3xl md:text-4xl font-bold">
              Frequently asked
              <br />
              <span className="italic">questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: "What's your design process like?",
                a: 'My process involves discovery, research, design, and delivery phases with regular check-ins and feedback loops.',
              },
              {
                q: 'How long does a project typically take?',
                a: 'Timeline varies based on project scope, typically 2-4 weeks for most projects.',
              },
              {
                q: 'Do you offer revisions?',
                a: "Yes, I offer revisions to ensure you're completely satisfied with the final result.",
              },
              {
                q: 'What file formats do you provide?',
                a: 'I provide source files, high-res exports, and web-optimized versions in various formats.',
              },
            ].map((faq, idx) => (
              <GlassyCard
                key={idx}
                className="p-5 rounded-xl transition-all duration-300 hover:translate-x-1 cursor-pointer"
              >
                <h3
                  className="font-semibold mb-2"
                  style={{ color: 'var(--text)' }}
                >
                  {faq.q}
                </h3>
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  {faq.a}
                </p>
              </GlassyCard>
            ))}
          </div>
        </section>

        {/* ==================== TESTIMONIALS ==================== */}
        <ReviewsSection />

        <GlassyDivider />

        {/* ==================== CTA SECTION ==================== */}
        <section className="animate-on-scroll py-20 px-4 transition-all duration-700 delay-600">
          <GlassyCard className="max-w-4xl mx-auto text-center p-12 rounded-3xl relative overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                opacity: 0.05,
              }}
            />

            <div className="relative z-10">
              <FiSmile
                className="w-12 h-12 mx-auto mb-4"
                style={{ color: 'var(--text)' }}
              />
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: 'var(--text)' }}
              >
                Ready to get started?
              </h2>
              <p
                className="mb-8 max-w-md mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                Let's create something amazing together. I'm just one message
                away.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <motion.a
                  whileHover={{ scale: 1.03, gap: '0.75rem' }}
                  whileTap={{ scale: 0.97 }}
                  href="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300"
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
                >
                  Start a Project
                  <FiArrowRight className="w-4 h-4" />
                </motion.a>
                <NavLink
                  to="/portfolio"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300"
                  style={{ border: '1px solid var(--border)' }}
                >
                  View Portfolio
                </NavLink>
              </div>
            </div>
          </GlassyCard>
        </section>

        {/* Inquiry Popup */}
        <InquiryPopup
          isOpen={isPopupOpen}
          onClose={() => {
            setIsPopupOpen(false);
            setSelectedPackage(null);
          }}
          packageData={selectedPackage}
          type="ready-made"
        />

        <style jsx>{`
          @keyframes shimmer {
            0% {
              transform: translateX(-100%) rotate(12deg);
            }
            100% {
              transform: translateX(200%) rotate(12deg);
            }
          }
        `}</style>
      </div>
    </>
  );
}

export default Services;
